import React, { useEffect, useMemo, useRef, useState } from 'react';
import GOOGLE_FONTS from '../../../../../../utils/googleFonts.json';
import { familyFromValue, loadGoogleFont, loadGoogleFonts } from '../../../../../../utils/googleFontLoader';
import './GoogleFontSelect.css';

const PAGE_SIZE = 40;

export const googleFontValue = (font) => (font ? `'${font.family}', ${font.generic}` : '');

export const GoogleFontPanel = ({ selectedFamily, onSelect, className = '', style }) => {
    const [search, setSearch] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const searchRef = useRef(null);

    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return GOOGLE_FONTS;
        return GOOGLE_FONTS.filter((f) => f.family.toLowerCase().includes(q));
    }, [search]);

    const visible = filtered.slice(0, visibleCount);
    const hasMore = filtered.length > visible.length;

    useEffect(() => {
        loadGoogleFonts(visible.map((f) => f.family));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, visibleCount]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setVisibleCount(PAGE_SIZE);
    };

    return (
        <div className={`googleFontSelect__panel ${className}`.trim()} style={style}>
            <input
                ref={searchRef}
                type="text"
                className="googleFontSelect__search"
                placeholder="Search fonts…"
                value={search}
                onChange={handleSearchChange}
            />
            <div className="googleFontSelect__list">
                <button
                    type="button"
                    className={`googleFontSelect__option${!selectedFamily ? ' googleFontSelect__option--selected' : ''}`}
                    onClick={() => onSelect(null)}
                >
                    Default
                </button>
                {visible.map((font) => (
                    <button
                        type="button"
                        key={font.family}
                        className={`googleFontSelect__option${selectedFamily === font.family ? ' googleFontSelect__option--selected' : ''}`}
                        style={{ fontFamily: googleFontValue(font) }}
                        onClick={() => onSelect(font)}
                    >
                        {font.family}
                    </button>
                ))}
                {visible.length === 0 && (
                    <div className="googleFontSelect__empty">No fonts match "{search}"</div>
                )}
                {hasMore && (
                    <button
                        type="button"
                        className="googleFontSelect__loadMore"
                        onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                    >
                        Load more ({filtered.length - visible.length} more)
                    </button>
                )}
            </div>
        </div>
    );
};

const GoogleFontSelect = ({ id, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    const selectedFamily = familyFromValue(value);

    useEffect(() => {
        if (selectedFamily) loadGoogleFont(selectedFamily);
    }, [selectedFamily]);

    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleSelect = (font) => {
        onChange(googleFontValue(font));
        setOpen(false);
    };

    return (
        <div className="googleFontSelect" ref={wrapRef}>
            <button
                type="button"
                id={id}
                className="googleFontSelect__trigger"
                style={selectedFamily ? { fontFamily: value } : undefined}
                onClick={() => setOpen((prev) => !prev)}
            >
                <span className="googleFontSelect__triggerLabel">{selectedFamily || 'Default'}</span>
                <span className="googleFontSelect__caret" aria-hidden="true">▾</span>
            </button>
            {open && <GoogleFontPanel selectedFamily={selectedFamily} onSelect={handleSelect} />}
        </div>
    );
};

export default GoogleFontSelect;
